import {
  Component,
  HostListener,
  Input,
  forwardRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  NgpRangeSlider,
  NgpRangeSliderRange,
  NgpRangeSliderThumb,
  NgpRangeSliderTrack,
} from 'ng-primitives/slider';
import { NgpFocus } from 'ng-primitives/interactions';

import { SuiFormControlBase } from '../shared/cva-helper';
import {
  SuiFormFieldControl,
  SUI_FORM_FIELD_CONTROL,
} from '../form-field/form-field-control';

/** A two-thumb range. Value is a `[low, high]` tuple; the primitive keeps low ≤ high. */
@Component({
  selector: 'sui-range-slider',
  standalone: true,
  imports: [
    NgpRangeSlider,
    NgpRangeSliderTrack,
    NgpRangeSliderRange,
    NgpRangeSliderThumb,
    NgpFocus,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SuiRangeSliderComponent),
      multi: true,
    },
    {
      provide: SUI_FORM_FIELD_CONTROL,
      useExisting: forwardRef(() => SuiRangeSliderComponent),
    },
  ],
  templateUrl: './sui-range-slider.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './sui-slider.component.scss',
})
export class SuiRangeSliderComponent
  extends SuiFormControlBase<[number, number]>
  implements SuiFormFieldControl
{
  private static _idCounter = 0;
  private readonly _uid = `sui-range-slider-${++SuiRangeSliderComponent._idCounter}`;
  private _focused = false;

  readonly controlType = 'range-slider';

  @Input() id = '';
  @Input() min = 0;
  @Input() max = 100;
  @Input() step = 1;

  @Input()
  set disabled(val: boolean) {
    this.setDisabledState(val);
  }
  get disabled(): boolean {
    return this.isDisabled();
  }

  // The primitive's value inputs are one-way; default to the full span until set.
  get low(): number {
    return this.value()?.[0] ?? this.min;
  }
  get high(): number {
    return this.value()?.[1] ?? this.max;
  }

  get effectiveId(): string {
    return this.id || this._uid;
  }

  get focused(): boolean {
    return this._focused;
  }
  get empty(): boolean {
    return this.value() == null;
  }
  get errorState(): boolean {
    return !!(this.ngControl?.invalid && this.ngControl?.touched);
  }

  onLowChange(value: number): void {
    this.setValue([value, this.high]);
  }
  onHighChange(value: number): void {
    this.setValue([this.low, value]);
  }

  @HostListener('focusin')
  onFocusIn(): void {
    this._focused = true;
  }

  @HostListener('focusout')
  onFocusOut(): void {
    this._focused = false;
    this.markTouched();
  }
}
